import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

// Site-wide announcement bar, driven by a single Firestore document
// (`config/announcement`) so the message can be turned on/off and edited from
// the /admin page WITHOUT a redeploy. Rendered globally in App.js, so it shows
// on every route (landing, dashboard, and each tool).
//
// The doc shape (all optional except `enabled`/`message`):
//   { enabled: bool, message: string, level: 'info'|'success'|'warn',
//     linkText: string, linkUrl: string, updatedAt: Timestamp }
//
// Reading this doc is the only network call the banner makes; it never sends
// any user/case data. Dismissal is remembered per-message in localStorage, so a
// new or edited message re-appears.

const LEVELS = {
  info:    { bg: 'linear-gradient(135deg, #4f46e5, #7c3aed)', fg: '#fff', link: '#e0e7ff' },
  success: { bg: 'linear-gradient(135deg, #059669, #0d9488)', fg: '#fff', link: '#d1fae5' },
  warn:    { bg: 'linear-gradient(135deg, #b45309, #d97706)', fg: '#fff', link: '#fef3c7' },
};

// Short stable id for a message so dismissing one doesn't hide the next.
function versionOf(a) {
  const base = `${a.message || ''}|${a.level || ''}|${a.linkUrl || ''}`;
  let h = 0;
  for (let i = 0; i < base.length; i++) { h = (h * 31 + base.charCodeAt(i)) | 0; }
  return String(h);
}

function AnnouncementBanner() {
  const [ann, setAnn] = useState(null);
  const [dismissedVer, setDismissedVer] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'config', 'announcement'),
      snap => setAnn(snap.exists() ? snap.data() : null),
      () => setAnn(null), // read blocked/unavailable — just show nothing
    );
    return unsub;
  }, []);

  if (!ann || !ann.enabled || !ann.message) return null;

  const version = versionOf(ann);
  const stored = dismissedVer ?? (() => {
    try { return localStorage.getItem('announce-dismissed'); } catch { return null; }
  })();
  if (stored === version) return null;

  const theme = LEVELS[ann.level] || LEVELS.info;
  const dismiss = () => {
    try { localStorage.setItem('announce-dismissed', version); } catch { /* ignore */ }
    setDismissedVer(version);
  };

  return (
    <div style={{
      background: theme.bg, color: theme.fg,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto', padding: '15px 48px 15px 24px',
        position: 'relative', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 12, flexWrap: 'wrap',
        fontSize: 16, fontWeight: 500, lineHeight: 1.5, textAlign: 'center',
      }}>
        <span>{ann.message}</span>
        {ann.linkUrl && ann.linkText && (
          <a
            href={ann.linkUrl}
            target={ann.linkUrl.startsWith('http') ? '_blank' : undefined}
            rel="noopener noreferrer"
            style={{ color: theme.link, fontWeight: 700, textDecoration: 'underline', whiteSpace: 'nowrap' }}
          >
            {ann.linkText} →
          </a>
        )}
        <button
          onClick={dismiss}
          aria-label="Dismiss announcement"
          style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', color: theme.fg, opacity: 0.85,
            cursor: 'pointer', fontSize: 24, lineHeight: 1, padding: 4,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default AnnouncementBanner;
